import { useEffect, useMemo, useState } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    Box,
    Text,
    Button,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { PaginationState, RowSelectionState, SortingState } from "@tanstack/react-table";
import { toast } from "react-toastify";

import DataTable from "components/v2/DataTable";
import apiDateFormat from "constants/apiDateFormat";
import { ESortOrder, ISortState } from "components/Table/types";
import formatOrderingAPI from "utils/formatOrderingAPI";
import useGetReports, {
    EReportsModules,
    TAdvancedReportOrderKeys,
    TMoneyListResults,
} from "api/useGetReports";
import { IDateRangeProps } from "components/DateRange";
import { useAppSelector } from "hooks/store";
import { selectUserData } from "store/auth/authSlice";
import { EMoneyListTabs, getColumns } from "../constants";
import { filterColumnsV2 } from "utils/filterColumns";
import { IProviderCheckList } from "types/IProviderCheckList";
import axiosInstance from "services/axiosInstance";
import { ENDPOINTS } from "api/endpoints";
import store from "store";
import { useDebounce } from "hooks";

interface ShowAllInventoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    dateRange: IDateRangeProps["value"];
    providers: IProviderCheckList[];
    onSaved?: () => void;
}

export function ShowAllInventoryModal({
    isOpen,
    onClose,
    dateRange,
    providers,
    onSaved,
}: ShowAllInventoryModalProps) {
    const userAuth = useAppSelector(selectUserData);

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [sortBy, setSortBy] = useState<SortingState>([]);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [isSaving, setIsSaving] = useState(false);

    const [quickSearch, setQuickSearch] = useState<string>("");

    const quickSearchApi = useDebounce<string>(quickSearch, 350);

    useEffect(() => {
        if (!isOpen) {
            setPage(0);
            setSortBy([]);
            setRowSelection({});
        }
    }, [isOpen]);

    const sortedBy: ISortState = useMemo(() => {
        if (!sortBy.length) return { column: null, order: ESortOrder.None };
        return {
            column: sortBy[0].id as TAdvancedReportOrderKeys,
            order: sortBy[0].desc ? ESortOrder.Descending : ESortOrder.Ascending,
        };
    }, [sortBy]);

    const fetchParameters = useMemo(
        () => ({
            module: EReportsModules.MoneyList,
            params: {
                date_from: format(dateRange.startDate, apiDateFormat),
                date_to: format(dateRange.endDate, apiDateFormat),
                dealership_uid: userAuth.dealerV2.uid,
                page: page + 1,
                page_size: pageSize,
                ordering: formatOrderingAPI<TAdvancedReportOrderKeys>(sortedBy),
                inventory: userAuth.inventory.value,
                search: quickSearchApi,
                // report_type: EMoneyListTabs.Conservative,
            },
        }),
        [
            dateRange.startDate,
            dateRange.endDate,
            userAuth.dealerV2.uid,
            userAuth.inventory.value,
            page,
            pageSize,
            sortedBy,
            quickSearchApi
        ]
    );

    const { data, isFetching } = useGetReports<EReportsModules.MoneyList>(fetchParameters);

    useEffect(() => {
        if (!data?.results) return;
        const checkedData: RowSelectionState = {};
        data.results.forEach((item) => {
            if (item.prioritize) {
                checkedData[item.vin] = true;
            }
        });
        setRowSelection(checkedData);
    }, [data?.results]);

    const columns = useMemo(
        () => filterColumnsV2(getColumns(userAuth.inventory.value, userAuth.dealerV2), providers),
        [providers, userAuth.dealerV2, userAuth.inventory.value]
    );

    const handleRowSelectionChange = (
        updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)
    ) => {
        setRowSelection((prev) => {
            const newSelection: RowSelectionState =
                typeof updaterOrValue === "function"
                    ? (updaterOrValue as (old: RowSelectionState) => RowSelectionState)(prev)
                    : updaterOrValue;

            const merged = { ...prev };

            Object.keys(prev).forEach((key) => {
                if (!(key in newSelection)) {
                    merged[key] = false;
                }
            });

            Object.entries(newSelection).forEach(([key, value]) => {
                merged[key] = value;
            });

            return merged;
        });
    };

    const onSavePrioritize = async () => {
        const selectedVins = Object.entries(rowSelection)
            .filter(([, v]) => v === true)
            .map(([vin]) => vin);

        const unselectedVins = Object.entries(rowSelection)
            .filter(([, v]) => v === false)
            .map(([vin]) => vin);

        const state = store.getState();
        const token = state.auth.accessToken;

        setIsSaving(true);
        try {
            const response = await axiosInstance.post(
                ENDPOINTS.PRIORITIZE_VEHICLES,
                {
                    dealership_uid: userAuth.dealerV2.uid,
                    added_vins: selectedVins,
                    removed_vins: unselectedVins,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.status !== 201) {
                toast.error("Failed to save prioritized vehicles");
                return;
            }

            const message: string[] = [];
            if (selectedVins.length > 0) message.push(`Added ${selectedVins.length} vehicles`);
            if (unselectedVins.length > 0) message.push(`Removed ${unselectedVins.length} vehicles`);
            toast.success(`${message.join(", ")} to prioritize list successfully`);

            onSaved?.();
            onClose();
        } catch {
            toast.error("Failed to save prioritized vehicles");
        } finally {
            setIsSaving(false);
        }
    };

    const onSearch = (search: string) => {
        setQuickSearch(search);
        setPage(0);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="6xl" scrollBehavior="inside">
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent borderRadius="xl">
                <ModalHeader borderBottom="1px solid" borderColor="gray.100" pb={3}>
                    <Text fontSize="lg" fontWeight="semibold">
                        {userAuth.inventory.value === 'new' ? "New All Inventory" : "Used All Inventory"}
                    </Text>
                    <Text fontSize="sm" fontWeight="normal" color="gray.500" mt={1}>
                        {data?.count !== undefined ? `${data.count} vehicles` : ""}
                    </Text>
                </ModalHeader>
                <ModalCloseButton top={4} />
                <ModalBody p={4}>
                    <Box display="flex" flexDirection="column" gap="15px" flex={1}>
                        <DataTable<TMoneyListResults>
                            data={data?.results || []}
                            rowCount={data?.count || 0}
                            onPageChange={({ pageIndex, pageSize: ps }: PaginationState) => {
                                setPage(pageIndex);
                                setPageSize(ps);
                            }}
                            sort={sortBy}
                            onSort={setSortBy}
                            columns={columns as any}
                            pageIndex={page}
                            pageSize={pageSize}
                            isLoading={isFetching}
                            pageCount={data?.total_pages}
                            showTotal={true}
                            label="Vehicles"
                            columnPinning={{ left: ["prioritize", "stock_number"] }}
                            enableMultiRowSelection={true}
                            enableRowSelection={true}
                            onRowSelectionChange={handleRowSelectionChange}
                            rowSelection={rowSelection}
                            getRowId={(row) => row.vin}
                            search={quickSearchApi}
                            onSearch={onSearch}
                        />
                    </Box>
                </ModalBody>
                <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        isLoading={isSaving}
                        loadingText="Saving..."
                        spinnerPlacement="end"
                        onClick={onSavePrioritize}
                    >
                        Save Priorities
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
